// Standard-mode controller: owns only shelf scrolling, arrow state and pager
// position. Product actions remain with the theme-rendered forms and modals.
(() => {
  const globalKey = 'CozyNookValueDeals';
  const sectionSelector = '.home-value-deals';

  if (window[globalKey]) {
    window[globalKey].initAll(document);
    return;
  }

  const instances = new WeakMap();

  class ValueDealsScroller {
    constructor(section) {
      this.section = section;
      this.shelf = section.querySelector('.home-value-deals__shelf');
      this.previousButton = section.querySelector('.home-value-deals__scroll-button--prev');
      this.nextButton = section.querySelector('.home-value-deals__scroll-button--next');
      this.progress = section.querySelector('.home-value-deals__pager');
      this.progressThumb = section.querySelector('.home-value-deals__pager-thumb');
      this.updateFrame = 0;
      this.isReady = Boolean(this.shelf && this.previousButton && this.nextButton && this.progress && this.progressThumb);

      if (!this.isReady) return;

      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.scrollPrevious = () => this.scrollShelf(-1);
      this.scrollNext = () => this.scrollShelf(1);

      this.previousButton.addEventListener('click', this.scrollPrevious);
      this.nextButton.addEventListener('click', this.scrollNext);
      this.shelf.addEventListener('scroll', this.scheduleUpdate, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
        this.resizeObserver.observe(this.shelf);
      } else {
        window.addEventListener('resize', this.scheduleUpdate, { passive: true });
      }

      this.updateControls();
    }

    updateControls() {
      this.updateFrame = 0;
      const maxScroll = Math.max(0, this.shelf.scrollWidth - this.shelf.clientWidth);
      const scrollPosition = Math.min(maxScroll, Math.max(0, this.shelf.scrollLeft));
      const isScrollable = maxScroll > 2;

      this.section.classList.toggle('home-value-deals--scrollable', isScrollable);
      this.previousButton.disabled = !isScrollable || scrollPosition <= 2;
      this.nextButton.disabled = !isScrollable || scrollPosition >= maxScroll - 2;

      const trackWidth = this.progress.clientWidth;
      if (!isScrollable || trackWidth <= 0) return;

      const thumbWidth = Math.max(18, trackWidth * (this.shelf.clientWidth / this.shelf.scrollWidth));
      const thumbTravel = Math.max(0, trackWidth - thumbWidth);
      const progressRatio = maxScroll > 0 ? scrollPosition / maxScroll : 0;

      this.progressThumb.style.width = `${thumbWidth}px`;
      this.progressThumb.style.transform = `translateX(${thumbTravel * progressRatio}px)`;
    }

    scheduleUpdate() {
      if (this.updateFrame) return;
      this.updateFrame = window.requestAnimationFrame(() => this.updateControls());
    }

    scrollShelf(direction) {
      const firstCard = this.shelf.querySelector('.home-value-deals__card');
      const shelfStyles = window.getComputedStyle(this.shelf);
      const gap = Number.parseFloat(shelfStyles.columnGap || shelfStyles.gap) || 0;
      const cardStep = firstCard ? firstCard.getBoundingClientRect().width + gap : this.shelf.clientWidth * 0.75;

      // Move by almost one viewport so customers retain visual context between groups.
      const visibleCards = Math.max(1, Math.floor(this.shelf.clientWidth / cardStep) - 1);
      const maxScroll = Math.max(0, this.shelf.scrollWidth - this.shelf.clientWidth);
      const targetScroll = Math.min(maxScroll, Math.max(0, this.shelf.scrollLeft + direction * cardStep * visibleCards));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.shelf.scrollTo({
        left: targetScroll,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    destroy() {
      if (!this.isReady) return;
      if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
      this.previousButton.removeEventListener('click', this.scrollPrevious);
      this.nextButton.removeEventListener('click', this.scrollNext);
      this.shelf.removeEventListener('scroll', this.scheduleUpdate);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.scheduleUpdate);
    }
  }

  function findSections(container) {
    const sections = [];
    if (container.matches?.(sectionSelector)) sections.push(container);
    container.querySelectorAll?.(sectionSelector).forEach((section) => sections.push(section));
    return sections;
  }

  function initAll(container = document) {
    findSections(container).forEach((section) => {
      if (instances.has(section)) return;
      const instance = new ValueDealsScroller(section);
      instances.set(section, instance);
    });
  }

  function destroyAll(container) {
    findSections(container).forEach((section) => {
      instances.get(section)?.destroy();
      instances.delete(section);
    });
  }

  window[globalKey] = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  // Shopify reloads sections without a full page refresh in the theme editor.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
